import { formatDateTime, formatRelativeTime } from '../lib/dateFormat';

/**
 * Structural timing shape shared by the page-level and document-level stage
 * blocks (`PageProcessingStageTiming` and `DocumentProcessingStageTiming` both
 * fit). Kept local so this component depends on the shape, not either schema.
 */
type StageTiming = {
  startedAt?: string;
  completedAt?: string;
  time?: number;
};

function formatDurationSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}

function buildHoverTitle(stage: StageTiming | undefined): string | undefined {
  const started = stage?.startedAt;
  const completed = stage?.completedAt;
  if (!started && !completed) {
    return undefined;
  }
  const lines: string[] = [];
  if (started) {
    lines.push(`Started: ${formatDateTime(started)}`);
  }
  if (completed) {
    lines.push(`Completed: ${formatDateTime(completed)}`);
  }
  return lines.join('\n');
}

type Props = {
  stage: StageTiming | undefined;
};

/**
 * Inline duration in seconds and "completed X ago" next to the stage name;
 * exact started/completed timestamps on hover via `title`. Omit `time` on the
 * stage to render only the relative label (e.g. when the caller shows its own
 * duration elsewhere).
 */
export function PipelineStageTiming({ stage }: Props) {
  const ms = stage?.time;
  const hasDuration = ms != null && !Number.isNaN(ms);
  const completedAt = stage?.completedAt;
  const title = buildHoverTitle(stage);

  if (!hasDuration && !completedAt) {
    return null;
  }

  const parts = [
    ...(hasDuration ? [formatDurationSeconds(ms)] : []),
    ...(completedAt ? [formatRelativeTime(completedAt)] : []),
  ];

  return (
    <span
      className={`text-xs font-normal tabular-nums text-zinc-500 dark:text-zinc-400 shrink-0 ${title ? 'cursor-help' : ''}`}
      title={title}
    >
      {parts.join(' · ')}
    </span>
  );
}
